import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesApiService, TenderOpportunityRecord, TenderProposalRecord } from './sales-api.service';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';

@Component({
  standalone: true,
  selector: 'ain-sales-tender-detail',
  imports: [
    AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, SelectModule, TableModule, DialogModule,
    CustomerSelectorComponent, CurrencySelectorComponent, JalaliDateInputComponent, SalesAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.tenders.form.createTitle') : t('sales.tenders.form.editTitle')" [description]="t('sales.tenders.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/tenders']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
            } @else {
              <button pButton type="button" [label]="t('sales.tenders.addProposal')" [disabled]="saving()" (click)="showProposalDialog.set(true)"></button>
            }
          </div>
        </header>
        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.tenders.fields.tenderNumber') }}</span><input pInputText [(ngModel)]="form.tenderNumber" /></label>
            <label><span>{{ t('sales.tenders.fields.title') }}</span><input pInputText [(ngModel)]="form.title" /></label>
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
            <label><span>{{ t('sales.tenders.fields.estimatedValue') }}</span><p-inputNumber [(ngModel)]="form.estimatedValue" [min]="0" /></label>
            <label><span>{{ t('sales.tenders.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <label><span>{{ t('sales.tenders.fields.dueDate') }}</span><ain-date-input [(ngModel)]="form.dueDateUtc" /></label>
          </section>
        } @else if (tender()) {
          <section class="card summary">
            <article><span>{{ t('sales.tenders.fields.tenderNumber') }}</span><strong>{{ tender()!.tenderNumber }}</strong></article>
            <article><span>{{ t('sales.tenders.fields.title') }}</span><strong>{{ tender()!.title }}</strong></article>
            <article><span>{{ t('columns.status') }}</span>
              <p-select [options]="statusOptions" optionValue="value" [(ngModel)]="statusForm" (ngModelChange)="updateStatus($event)" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </article>
            <article><span>{{ t('sales.tenders.fields.estimatedValue') }}</span><strong>{{ tender()!.estimatedValue | ainAmount }} {{ tender()!.currencyCode }}</strong></article>
          </section>
          <h3>{{ t('sales.tenders.tabs.proposals') }}</h3>
          <p-table [value]="proposals()" [paginator]="true" [rows]="12">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('sales.tenders.fields.proposalNumber') }}</th>
                <th>{{ t('sales.tenders.fields.version') }}</th>
                <th>{{ t('sales.tenders.fields.proposalAmount') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.proposalNumber }}</td>
                <td>v{{ row.versionNumber }}</td>
                <td>{{ row.proposalAmount | ainAmount }} {{ tender()?.currencyCode }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
        @if (!isCreateMode() && tender()) {
          <ain-sales-ai-copilot-panel entityType="TenderOpportunity" [entityId]="tender()!.id" defaultRecommendationType="WinProbability" />
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
    <p-dialog [header]="t('sales.tenders.addProposal')" [visible]="showProposalDialog()" (visibleChange)="showProposalDialog.set($event)" [modal]="true" [style]="{width:'360px'}">
      <label><span>{{ t('sales.tenders.fields.proposalNumber') }}</span><input pInputText [(ngModel)]="proposalForm.proposalNumber" /></label>
      <label><span>{{ t('sales.tenders.fields.proposalAmount') }}</span><p-inputNumber [(ngModel)]="proposalForm.proposalAmount" [min]="0" /></label>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="submitProposal()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px;background:#fff}.toolbar{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px}.back-link{color:#1d4ed8;font-weight:600;text-decoration:none}.grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}label{display:grid;gap:6px}.summary{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;font-size:14px;align-items:start}.summary article{display:grid;gap:4px}.summary span{color:#6b7280;font-size:12px}.error{color:#b91c1c}h3{margin:0 0 8px;font-size:15px}`]
})
export class SalesTenderDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly tender = signal<TenderOpportunityRecord | null>(null);
  readonly proposals = signal<TenderProposalRecord[]>([]);
  readonly showProposalDialog = signal(false);

  statusForm = 'Open';
  statusOptions = [
    { value: 'Open', labelKey: 'sales.tenders.status.open' },
    { value: 'InProgress', labelKey: 'sales.tenders.status.inProgress' },
    { value: 'Submitted', labelKey: 'sales.tenders.status.submitted' },
    { value: 'Won', labelKey: 'sales.tenders.status.won' },
    { value: 'Lost', labelKey: 'sales.tenders.status.lost' },
    { value: 'Closed', labelKey: 'sales.tenders.status.closed' }
  ];

  form = { tenderNumber: '', title: '', customerAccountId: null as string | null, estimatedValue: 0, currencyCode: 'IRR', dueDateUtc: null as string | null };
  proposalForm = { proposalNumber: '', proposalAmount: 0 };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('tenderId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) { this.loading.set(false); return; }
    this.loadTender(id!);
  }

  loadTender(id: string): void {
    this.loading.set(true);
    this.api.getTender(id).subscribe({
      next: t => { this.tender.set(t); this.statusForm = t.status; },
      error: () => this.errorMessage.set(this.t('sales.tenders.errorLoad'))
    });
    this.api.listTenderProposals(id).subscribe({
      next: rows => { this.proposals.set(rows); this.loading.set(false); },
      error: () => { this.errorMessage.set(this.t('sales.tenders.errorLoad')); this.loading.set(false); }
    });
  }

  save(): void {
    if (!this.form.tenderNumber.trim()) {
      this.errorMessage.set(this.t('sales.tenders.errorSave'));
      return;
    }
    this.saving.set(true);
    this.api.createTender({
      tenderNumber: this.form.tenderNumber,
      title: this.form.title || this.form.tenderNumber,
      customerAccountId: this.form.customerAccountId,
      dueDateUtc: this.form.dueDateUtc,
      estimatedValue: this.form.estimatedValue,
      currencyCode: this.form.currencyCode,
      notes: null
    }).subscribe({
      next: t => void this.router.navigate(['/sales/tenders', t.id]),
      error: () => { this.errorMessage.set(this.t('sales.tenders.errorSave')); this.saving.set(false); }
    });
  }

  submitProposal(): void {
    const id = this.route.snapshot.paramMap.get('tenderId')!;
    this.saving.set(true);
    this.api.createTenderProposal(id, {
      proposalNumber: this.proposalForm.proposalNumber,
      proposalAmount: this.proposalForm.proposalAmount,
      notes: null,
      submitImmediately: true
    }).subscribe({
      next: () => { this.showProposalDialog.set(false); this.saving.set(false); this.loadTender(id); },
      error: () => { this.errorMessage.set(this.t('sales.tenders.proposalError')); this.saving.set(false); }
    });
  }

  updateStatus(status: string): void {
    const id = this.route.snapshot.paramMap.get('tenderId')!;
    this.api.updateTenderStatus(id, status).subscribe({
      next: t => this.tender.set(t),
      error: () => this.errorMessage.set(this.t('sales.tenders.errorLoad'))
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
